<template>
    <div>
        <div class="box view-nav-bar">
            <div class="d-flex align-items-center w-100">
                <div class="flex-grow-1">
                    <router-link to="/admissions/movie" class="mr-3">
                        <icon name="chevron-left"/> Back
                    </router-link>
                    <strong>{{ bucket.title || 'Movix ' + movixId }}</strong>
                    <span class="text-muted ml-2">
                        — Movix <movix-link :id="movixId"/>
                        <span v-if="bucket.movie_tag"> · {{ bucket.movie_tag }}</span>
                        <span v-if="bucket.released_at"> · released {{ bucket.released_at }}</span>
                        <span v-if="bucket.imdb_id"> · <a :href="'https://www.imdb.com/title/' + bucket.imdb_id" target="_blank" rel="noopener">{{ bucket.imdb_id }}</a></span>
                    </span>
                </div>
                <router-link :to="'/history/' + movixId" class="btn btn-sm btn-outline-secondary">History</router-link>
            </div>
        </div>

        <div v-if="loading" class="box">
            <div class="box-content text-center text-muted py-4">Loading…</div>
        </div>

        <div v-else-if="!totals.weeks" class="box">
            <div class="box-content text-center text-muted py-4">No admissions found for Movix id {{ movixId }}.</div>
        </div>

        <template v-else>
            <div class="box">
                <div class="box-content">
                    <table class="shx-table w-100 mt-2">
                        <thead>
                            <tr>
                                <th></th>
                                <th class="text-right">Admissions</th>
                                <th class="text-right shx-th-120">Avg / week</th>
                                <th class="text-right shx-th-100">% of all</th>
                                <th class="text-right shx-th-100"># Weeks</th>
                                <th class="shx-th-200">First – last week</th>
                                <th class="shx-th-100">Peak week</th>
                                <th class="text-right shx-th-100">Max cinemas</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>All sessions</strong> <span class="text-muted small ml-2">every sales house</span></td>
                                <td class="text-right">{{ formatInt(totals.admissions) }}</td>
                                <td class="text-right">{{ formatInt(totals.admissions / totals.weeks) }}</td>
                                <td class="text-right">100%</td>
                                <td class="text-right">{{ totals.weeks }}</td>
                                <td>{{ totals.first_week }} – {{ totals.last_week }}</td>
                                <td>{{ totals.peak_week }}</td>
                                <td class="text-right">{{ totals.cinemas }}</td>
                            </tr>
                            <tr>
                                <td><strong>Preshow sessions</strong> <span class="text-muted small ml-2">sales house BF</span></td>
                                <td class="text-right">{{ formatInt(totals.admissions_preshow) }}</td>
                                <td class="text-right">{{ formatInt(totals.admissions_preshow / totals.weeks) }}</td>
                                <td class="text-right">{{ totals.pct_preshow !== null ? totals.pct_preshow + '%' : '—' }}</td>
                                <td colspan="4"></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="box">
                <div class="box-content">
                    <heading upper="Active weeks" :lower="weeks.length + ' cinema weeks with admissions'" border/>
                    <table class="shx-table w-100 mt-2">
                        <thead>
                            <tr>
                                <th class="shx-th-50">#</th>
                                <th class="shx-th-100">Week</th>
                                <th class="shx-th-200">Dates</th>
                                <th class="text-right shx-th-100"># Cinemas</th>
                                <th class="text-right">Admissions</th>
                                <th class="text-right shx-th-100">% of movie</th>
                                <th class="shx-th-200"></th>
                                <th class="text-right shx-th-120">Preshow (BF)</th>
                                <th class="text-right shx-th-100">Preshow %</th>
                                <th class="text-right shx-th-100">Real %</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(week, index) in weeks" :key="week.week_code">
                                <td class="text-muted">{{ index + 1 }}</td>
                                <td>
                                    <router-link :to="'/admissions/year/' + week.week_code.substr(0, 4) + '/' + week.week_code">{{ week.week_code }}</router-link>
                                </td>
                                <td class="text-muted">
                                    <span v-if="week.week_start">{{ week.week_start }} – {{ week.week_end }}</span>
                                    <span v-else>—</span>
                                </td>
                                <td class="text-right">{{ week.cinemas }}</td>
                                <td class="text-right">{{ formatInt(week.admissions) }}</td>
                                <td class="text-right">{{ pct(week.admissions, totals.admissions) }}</td>
                                <td>
                                    <div class="progress" style="height: 8px;" :title="formatInt(week.admissions)">
                                        <div class="progress-bar" role="progressbar" :style="{ width: barWidth(week) + '%' }"></div>
                                    </div>
                                </td>
                                <td class="text-right">{{ formatInt(week.admissions_preshow) }}</td>
                                <td class="text-right">{{ week.pct_preshow !== null ? week.pct_preshow + '%' : '—' }}</td>
                                <td class="text-right">
                                    <span>{{ week.real_weighted != null ? week.real_weighted + '%' : '—' }}</span>
                                    <span v-if="week.real_weighted != null && week.real_weighted <= 90"
                                          class="badge badge-warning ml-1"
                                          :title="'Minimum real: ' + week.real_min + '%'">⚠</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <p class="text-muted small mt-2 mb-0">
                        One row per <code>week_code</code> with an <code>admissions_week</code> row for this Movix id, all sales houses;
                        <strong>Preshow</strong> = rows with <code>saleshouse = BF</code>. <strong>Real %</strong> is the
                        admissions-weighted average of <code>real</code>: 97.5 = fully compiled from Cinedata, lower = partly estimated.
                        Same data as JSON: <code>GET /api/v1/admissions/movie/{{ movixId }}</code>.
                    </p>
                </div>
            </div>

            <div class="box">
                <div class="box-content">
                    <heading upper="Source titles" :lower="titles.length + ' Cinedata titles mapped to this movie'" border/>
                    <table v-if="titles.length" class="shx-table w-100 mt-2">
                        <thead>
                            <tr>
                                <th>Source title</th>
                                <th class="text-right shx-th-100"># Days</th>
                                <th class="text-right shx-th-100"># Cinemas</th>
                                <th class="text-right">Admissions</th>
                                <th class="text-right shx-th-100">% of movie</th>
                                <th class="shx-th-120">First day</th>
                                <th class="shx-th-120">Last day</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="title in titles" :key="title.source_title">
                                <td><code>{{ title.source_title }}</code></td>
                                <td class="text-right">{{ title.days }}</td>
                                <td class="text-right">{{ title.cinemas }}</td>
                                <td class="text-right">{{ formatInt(title.admissions) }}</td>
                                <td class="text-right">{{ pct(title.admissions, titlesAdmissions) }}</td>
                                <td>{{ title.first_day }}</td>
                                <td>{{ title.last_day }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <p v-else class="text-muted small mb-0">
                        No daily rows: the weekly admissions of this movie are estimated or were imported from the legacy archive.
                    </p>
                    <p v-if="titles.length" class="text-muted small mt-2 mb-0">
                        The titles as reported by Cinedata in <code>admissions_day</code>, before mapping to a Movix id.
                        Daily admissions are without the 2.5% free-ticket multiplier and without estimated weeks, so
                        they do not add up to the weekly total above.
                    </p>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import axios from 'axios';
import Heading from '../../partials/Heading';
import MovixLink from '../../partials/MovixLink';

export default {
    name: 'AdmissionsMovie',
    components: {
        Heading,
        MovixLink,
    },
    props: ['movixId'],
    data() {
        return {
            loading: true,
            bucket: {},
            totals: {},
            weeks: [],
            titles: [],
        };
    },
    computed: {
        peakAdmissions() {
            return this.weeks.reduce((max, w) => Math.max(max, w.admissions || 0), 0);
        },
        titlesAdmissions() {
            return this.titles.reduce((sum, t) => sum + (t.admissions || 0), 0);
        },
    },
    created() {
        this.load();
    },
    watch: {
        movixId() {
            this.load();
        },
    },
    methods: {
        load() {
            this.loading = true;
            axios
                .get(`/admissions/movie/${this.movixId}`)
                .then(response => {
                    this.bucket = response.data.data.bucket || {};
                    this.totals = response.data.data.totals || {};
                    this.weeks = response.data.data.weeks || [];
                    this.titles = response.data.data.titles || [];
                })
                .catch(error => {
                    this.$toast.error(error.response?.data?.error?.message || error.message);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        barWidth(week) {
            return this.peakAdmissions ? Math.round((week.admissions || 0) / this.peakAdmissions * 100) : 0;
        },
        pct(part, total) {
            return total ? (Math.round(part / total * 1000) / 10) + '%' : '—';
        },
        formatInt(v) {
            return Math.round(v || 0).toLocaleString();
        },
    },
};
</script>
